// The grounded "control" a clause renders below itself — a distribution, value pills, or segments.
// The payload shapes are the model's Row* types (../types); the helpers over them live here.

import type { ConditionValue, OperatorValue, PillValue, RowControl, RowSegment, RowSegments } from '../types';

export type {
  DisplayUnit,
  DistributionBin,
  DistributionStats,
  PillValue,
  RowControl,
  RowDistribution,
  RowPills,
  RowQuantiles,
  RowSegment,
  RowSegments,
} from '../types';

/** A loose partial edit a pill/segment applies to a predicate — kept wide; the row validates it on apply. */
export interface PredicatePatch {
  operator?: OperatorValue;
  value?: ConditionValue;
}

export function attributeSegments(segments: RowSegment[], total?: number): RowSegments {
  return { kind: 'segments', segments, total };
}

export function insightHasData(control: RowControl): boolean {
  switch (control.kind) {
    case 'distribution':
      return control.bins.length > 0;
    case 'pills':
      return control.values.length > 0;
    case 'segments':
      return control.segments.length > 0;
    case 'quantiles':
      return control.quantiles.length > 0;
  }
}

/** Structural equality that ignores object key order (unlike JSON.stringify). */
function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    return a.every((x, i) => deepEqual(x, b[i]));
  }
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => deepEqual((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k]));
}

/** Whether the predicate already contains every field a segment's patch sets (so its pill reads active). */
export function segmentActive(current: PredicatePatch, patch: PredicatePatch): boolean {
  const r = current as Record<string, unknown>;
  const p = patch as Record<string, unknown>;
  return Object.keys(patch).every((k) => deepEqual(r[k], p[k]));
}

/** The raw table value(s) a pill stands for: every folded spelling for a group, else its own value. */
export function pillRaws(pill: PillValue): string[] {
  return pill.mergedFrom && pill.mergedFrom.length > 0 ? pill.mergedFrom.map((m) => m.raw) : [pill.value];
}
